import AIRecommendations from "@/Pages/Landing/Partials/AIRecommendations";
import { Clients } from "@/Pages/Landing/Partials/Clients";
import FAQs from "@/Pages/Landing/Partials/FAQs";
import FamilyDiscoverySearch from "@/Pages/Landing/Partials/FamilyDiscoverySearch";
import GuestFooter from "@/Pages/Landing/Partials/GuestFooter";
import HeroSplit from "@/Pages/Landing/Partials/HeroSplit";
import LandingPricing from "@/Pages/Landing/Partials/LandingPricing";
import PaperTreeLandingSection from "@/Pages/Landing/Partials/PaperTreeLandingSection";
import Stats from "@/Pages/Landing/Partials/Stats";
import Testimonials from "@/Pages/Landing/Partials/Testimonials";
import { PromotionSection } from "@/components/marketing/PromotionSection";
import { Text } from "@/components/typography/Text";
import { Button } from "@/components/ui/button";
import type { PublicTestimonial } from "@/features/testimonials/testimonial";
import type { FrontendPackage } from "@/types/models";
import { TestTubeIcon, Tree06Icon, UserSwitchIcon } from "@hugeicons-pro/core-stroke-standard";
import { HugeiconsIcon } from "@hugeicons/react";
import { Link } from "@inertiajs/react";

interface BranchName {
  name: string;
  id: number;
  nodeCount: number;
  roundedCount: number;
}

interface WelcomeProps {
  isLocal: boolean;
  nodesCount: number;
  tenantsCount: number;
  usersCount: number;
  freeNodesCount: number;
  branchesNames: BranchName[];
  landingPackages: FrontendPackage[];
  testimonials?: PublicTestimonial[];
}

const EMPTY_TESTIMONIALS: PublicTestimonial[] = [];

function LocalLoginBanner() {
  return (
    <aside className="border-b border-green-100 bg-green-50/80">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:justify-between md:px-8 lg:px-12">
        <div>
          <Text size="small" className="font-bold text-green-900">
            اختصارات الدخول المحلي
          </Text>
          <Text size="helper" className="mt-1 text-green-800/80">
            للتطوير المحلي فقط، بدون دخول الأفراد.
          </Text>
        </div>

        <div className="flex flex-wrap gap-2">
          <Link href="/dev-login">
            <Button variant="outline" size="sm" className="border-green-200 bg-white text-green-900 hover:bg-green-100">
              <HugeiconsIcon icon={UserSwitchIcon} size={16} color="currentColor" strokeWidth={1.5} />
              دخول Dev
            </Button>
          </Link>
          <Link href="/qa-login">
            <Button variant="outline" size="sm" className="border-green-200 bg-white text-green-900 hover:bg-green-100">
              <HugeiconsIcon icon={TestTubeIcon} size={16} color="currentColor" strokeWidth={1.5} />
              دخول QA
            </Button>
          </Link>
          <Link href="/large-tree-login">
            <Button variant="outline" size="sm" className="border-blue-200 bg-white text-blue-900 hover:bg-blue-50">
              <HugeiconsIcon icon={Tree06Icon} size={16} color="currentColor" strokeWidth={1.5} />
              الشجرة الكبيرة
            </Button>
          </Link>
          <Link href="/huge-tree-login">
            <Button
              variant="outline"
              size="sm"
              className="border-violet-200 bg-white text-violet-900 hover:bg-violet-50"
            >
              <HugeiconsIcon icon={Tree06Icon} size={16} color="currentColor" strokeWidth={1.5} />
              الشجرة الضخمة
            </Button>
          </Link>
          <Link href="/node-near-limit-login">
            <Button variant="outline" size="sm" className="border-amber-200 bg-white text-amber-900 hover:bg-amber-50">
              <HugeiconsIcon icon={Tree06Icon} size={16} color="currentColor" strokeWidth={1.5} />
              قرب حد العقد
            </Button>
          </Link>
          <Link href="/node-limit-login">
            <Button variant="outline" size="sm" className="border-red-200 bg-white text-red-900 hover:bg-red-50">
              <HugeiconsIcon icon={Tree06Icon} size={16} color="currentColor" strokeWidth={1.5} />
              حد العقد
            </Button>
          </Link>
        </div>
      </div>
    </aside>
  );
}

export default function Welcome({
  isLocal,
  nodesCount,
  tenantsCount,
  usersCount,
  freeNodesCount,
  branchesNames,
  landingPackages,
  testimonials = EMPTY_TESTIMONIALS,
}: WelcomeProps) {
  return (
    <div className="relative min-h-screen w-full bg-white bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-50 via-white to-gray-50/80">
      <div className="text-gray-800">
        {isLocal ? <LocalLoginBanner /> : null}

        <HeroSplit freeNodesCount={freeNodesCount} />

        <PromotionSection />

        <FamilyDiscoverySearch />

        <Clients branchesNames={branchesNames} />

        <section className="py-8 md:py-10">
          <Stats nodesCount={nodesCount} tenantsCount={tenantsCount} usersCount={usersCount} />
        </section>

        <Testimonials testimonials={testimonials} />

        <section className="py-12 md:py-16">
          <AIRecommendations />
        </section>

        <LandingPricing packages={landingPackages} freeNodesCount={freeNodesCount} />

        <PaperTreeLandingSection />

        <section className="py-12 md:py-16">
          <FAQs />
        </section>

        <hr />
        <section className="py-8 md:py-10">
          <GuestFooter />
        </section>
      </div>
    </div>
  );
}
